// Client-only "quick login" helpers: PIN hash + optional WebAuthn biometric.
// Storage lives on the device — clearing site data or logging out clears it.
const CREDS_KEY = "talabya:remembered-login";
const PIN_KEY = "talabya:quick-pin";
const BIO_KEY = "talabya:bio-cred-id";

// ---------- Saved credentials (identifier + password) ----------
export function loadSavedCreds() {
  try {
    const raw = localStorage.getItem(CREDS_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}
export function saveCreds(identifier, password) {
  try { localStorage.setItem(CREDS_KEY, JSON.stringify({ identifier, password })); } catch { /* noop */ }
}
export function clearCreds() {
  try { localStorage.removeItem(CREDS_KEY); } catch { /* noop */ }
}

// ---------- PIN (4-6 digits, hashed) ----------
async function _sha256(text) {
  const buf = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(text));
  return Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, "0")).join("");
}
export async function setPin(pin) {
  if (!/^\d{4,6}$/.test(pin)) throw new Error("PIN يجب أن يكون من 4 إلى 6 أرقام");
  const hash = await _sha256("talabya::" + pin);
  localStorage.setItem(PIN_KEY, hash);
}
export function hasPin() { return !!localStorage.getItem(PIN_KEY); }
export function clearPin() { localStorage.removeItem(PIN_KEY); }
export async function verifyPin(pin) {
  const saved = localStorage.getItem(PIN_KEY);
  if (!saved) return false;
  const hash = await _sha256("talabya::" + pin);
  return hash === saved;
}

// ---------- Biometric (WebAuthn platform authenticator, device-bound) ----------
export async function bioAvailable() {
  try {
    if (!window.PublicKeyCredential) return false;
    return await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch { return false; }
}
export function hasBio() { return !!localStorage.getItem(BIO_KEY); }
export function clearBio() { localStorage.removeItem(BIO_KEY); }

function _b64uToBytes(b64u) {
  const b64 = b64u.replace(/-/g, "+").replace(/_/g, "/") + "=".repeat((4 - b64u.length % 4) % 4);
  const bin = atob(b64);
  const u8 = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) u8[i] = bin.charCodeAt(i);
  return u8;
}
function _bytesToB64u(bytes) {
  let bin = ""; for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}
function _randomChallenge() {
  const c = new Uint8Array(32); crypto.getRandomValues(c); return c;
}

export async function registerBio(userIdText) {
  const rpId = window.location.hostname;
  const uid = new TextEncoder().encode(userIdText || `user-${Date.now()}`);
  const cred = await navigator.credentials.create({
    publicKey: {
      challenge: _randomChallenge(),
      rp: { name: "Talabya", id: rpId },
      user: { id: uid, name: userIdText || "talabya", displayName: "Talabya" },
      pubKeyCredParams: [{ type: "public-key", alg: -7 }, { type: "public-key", alg: -257 }],
      authenticatorSelection: { authenticatorAttachment: "platform", userVerification: "required", residentKey: "preferred" },
      timeout: 60000,
      attestation: "none",
    },
  });
  if (!cred?.rawId) throw new Error("لم يتم إنشاء بصمة");
  const id = _bytesToB64u(new Uint8Array(cred.rawId));
  localStorage.setItem(BIO_KEY, id);
  return id;
}

export async function verifyBio() {
  const id = localStorage.getItem(BIO_KEY);
  if (!id) throw new Error("لم يتم تسجيل بصمة على هذا الجهاز");
  const rpId = window.location.hostname;
  await navigator.credentials.get({
    publicKey: {
      challenge: _randomChallenge(),
      rpId,
      allowCredentials: [{ id: _b64uToBytes(id), type: "public-key" }],
      userVerification: "required",
      timeout: 60000,
    },
  });
  // Success = OS-verified biometric. We treat this as "unlock stored creds".
  return true;
}
